import React, { useEffect, useRef, useState } from 'react'
import SecTitleWithIconDesc from './components/secTitleWithIconDesc'
import LogoIcon from '../../assets/icons/LogoIcon'
import { useTheme } from '../../middleware/ThemeContext'
import { SecCard } from './SecLivePreview'
import { FieldLabel } from '../../components/FormBuilder/FieldSettings/FieldSettingsModal'
import CustomInput from '../../components/form/CustomInput'
import CustomInputURL from '../../components/form/CustomInputURL'
import StyledImageUpload from '../../components/StyledComponents/StyledImageUpload'
import FormikImageUpload from '../../components/Formik/FormikImageUpload'
import { useToast } from '../../components/toast/ToastContext'
import { Formik, FormikProvider } from 'formik'
import StyledColorPicker from '../../components/StyledComponents/StyledColorPicker'
import FontPicker from './components/FontPicker'
import * as Yup from 'yup'; // optional, for validation
import { WebsiteFormValues } from '../../types/types'
import { useLogoPreSignedUrlMutation } from '../../api/pageBuilderApiSlice'
import StyledText from '../../components/StyledComponents/StyledText'
import { FavIconRecommendationCard } from './WebsiteConfiguration/SettingsComponents/LogoCard'
import { font } from '../../utils/themes'

interface UploadedFile {
    file: File;
    name: string;
    type: string;
    rename: string;
    progress: number;
}

// Define form values type


// Validation schema
const websiteValidationSchema = Yup.object({
    websiteName: Yup.string().required('Website name is required'),
    websiteURl: Yup.string().url('Invalid URL').nullable(),
});

interface UploadedFile {
    file: File;
    name: string;
    type: string;
    rename: string;
    progress: number;
}

const SecSettings = ({
    setWebsiteDetails,
    websiteDetails,
    isResponsive
}: {
    websiteDetails: WebsiteFormValues,
    setWebsiteDetails: React.Dispatch<React.SetStateAction<WebsiteFormValues>>,
    isResponsive?: boolean
}) => {
    const { theme } = useTheme();
    const showToast = useToast();
    const [hasUploaded, setHasUploaded] = useState(false);
    const [logoPreSignedUrl] = useLogoPreSignedUrlMutation();
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadKey, setUploadKey] = useState(0);

    const handleUpload = async (
        file: UploadedFile,
        onProgress?: (percent: number) => void
    ): Promise<{ key: string }> => {
        const parts = file.name?.split(".");
        const extension = parts.pop();

        try {
            const { data } = await logoPreSignedUrl({
                fileName: file.name,
                fileType: extension,
            });

            if (!data?.url) {
                showToast("Error", "Could not generate upload URL", "error");
                return { key: '' };
            }

            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open(data.method, data.url, true);
                xhr.setRequestHeader("Content-Type", file.type);

                // 👇 Add progress tracking
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable && onProgress) {
                        const percent = Math.round((event.loaded / event.total) * 100);
                        onProgress(percent);
                    }
                };

                xhr.onload = () => {
                    if (xhr.status === 200 || xhr.status === 204) {
                        resolve({ key: data.key });
                    } else {
                        reject(new Error("Upload failed"));
                    }
                };

                xhr.onerror = () => reject(new Error("Network error"));
                xhr.send(file.file);
            });
        } catch (err) {
            showToast("Error", "Upload preparation failed", "error");
            return { key: '' };
        }
    };





    return (
        <Formik
            initialValues={websiteDetails}
            validationSchema={websiteValidationSchema}
            enableReinitialize={true}
            onSubmit={(values) => {
                console.log('Form submitted:', values);
            }}
        >
            {({ values, setFieldValue }) => {
                // setWebsiteDetailsRef.current(values);

                const handleFileChange = (files: UploadedFile[]) => {
                    if (!files.length) return;
                    const file = files[0];

                    // 🔴 5MB size check
                    if (file.file.size > 5 * 1024 * 1024) {
                        showToast("Error", "Logo size must be less than 5MB", "error");
                        setFieldValue('logo', '');
                        setFieldValue('logoPreview', '');
                        setHasUploaded(false);
                        setUploadProgress(0);
                        setIsUploading(false);
                        setWebsiteDetails(prev => ({ ...prev, logo: '', logoPreview: '', logoFileName: '', logoFileSize: 0 }));
                        setUploadKey(prev => prev + 1);
                        return;
                    }

                    const reader = new FileReader();
                    reader.onload = () => {
                        if (reader.result) {
                            setFieldValue('logoPreview', reader.result);
                            setWebsiteDetails(prev => ({ ...prev, logoPreview: reader.result as string }));
                        }
                    };
                    reader.readAsDataURL(file.file);

                    setIsUploading(true);
                    setUploadProgress(0);

                    handleUpload(file, setUploadProgress)
                        .then(({ key }) => {
                            if (key) {
                                setFieldValue('logo', key);
                                setWebsiteDetails(prev => ({ ...prev, logo: key, logoFileName: file.name, logoFileSize: file.file.size }));
                                setHasUploaded(true);
                            }
                        })
                        .catch(() => {
                            showToast('Error', 'Failed to upload logo', 'error');
                        })
                        .finally(() => {
                            setIsUploading(false);
                        });
                };

                const handleRemoveLogo = () => {
                    setFieldValue('logo', '');
                    setFieldValue('logoPreview', '');
                    setHasUploaded(false);
                    setWebsiteDetails(prev => ({ ...prev, logo: '', logoPreview: '', logoFileName: '', logoFileSize: 0 }));
                };

                return (
                    <SecCard theme={theme}>
                        <div className='p-4' style={{ borderBottom: `1px solid ${theme.CorporateTheme.colors.border}` }}>
                            <SecTitleWithIconDesc
                                fontSize={isResponsive ? '14px' : '16px'}
                                descfontSize={isResponsive ? '12px' : '14px'}
                                icon={<LogoIcon color={theme.CorporateTheme.colors.primary} />}
                                title='Let’s Setup Your Website'
                                desc='Customize your brand Identity & we will handle the rest.'
                            />
                        </div>
                        <div className='p-4'>
                            {/* Website Name */}
                            <div className="pb-3">
                                <StyledText fontSize={isResponsive ? '12px' : '14px'} fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className='mb-2'>
                                    Website Name <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>
                                </StyledText>
                                <CustomInput
                                    type="text"
                                    placeholder="Enter website name"
                                    value={values.websiteName}
                                    onChange={(val: string) => {
                                        setFieldValue('websiteName', val);
                                        setWebsiteDetails(prev => ({ ...prev, websiteName: val }));
                                    }}
                                />
                            </div>

                            {/* Website URL */}
                            <div className='pb-3'>
                                <StyledText fontSize={isResponsive ? '12px' : '14px'} fontWeight={600} className='mb-2' textColor={theme.CorporateTheme.colors.headerColor}>Website Url</StyledText>
                                <CustomInputURL
                                    value={values.websiteURl}
                                    onChange={(val) => {
                                        setFieldValue('websiteURl', val);
                                        setWebsiteDetails(prev => ({ ...prev, websiteURl: val }));
                                    }}
                                    validationType='subdomain'
                                />
                            </div>

                            {/* Logo Upload */}
                            <div className='pb-3'>
                                <div className='d-flex align-items-center justify-content-between pb-2'>
                                    <StyledText className='mb-2' fontSize={isResponsive ? '12px' : '14px'} fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>
                                        Upload Logo <span style={{ color: theme.CorporateTheme.colors.textSecondary, fontSize: '11px' }}>(Optional)</span>
                                    </StyledText>
                                    <div className='d-flex align-items-center gap-1'>
                                        <StyledText fontStyle='italic' fontSize={isResponsive ? '12px' : '14px'} fontWeight={700} textColor={theme.CorporateTheme.colors.count}>Recommended : </StyledText>

                                        <FavIconRecommendationCard theme={theme}>
                                            <StyledText fontStyle='italic' fontFamily={font.lato} fontSize='12px' fontWeight={700} textColor={theme.CorporateTheme.colors.headerColor}>Less Then 5MB</StyledText>

                                        </FavIconRecommendationCard>
                                    </div>
                                </div>
                                <FormikImageUpload
                                    accept=".png, .jpg, .jpeg, .webp"
                                    key={uploadKey}
                                    name="logo"
                                    isResponsive
                                    onChange={handleFileChange}
                                    previewUrl={values.logoPreview || values.logo}
                                    uploadProgress={uploadProgress}
                                    isUploading={isUploading}
                                    removeFile={handleRemoveLogo}
                                    setHasUploaded={setHasUploaded}
                                    existingFileName={websiteDetails?.logoFileName}
                                    existingFileSize={websiteDetails?.logoFileSize}

                                />
                            </div>

                            {/* Color Palette */}
                            <div className='pb-4' style={{ borderBottom: `1px solid ${theme.CorporateTheme.colors.border}` }}>
                                <StyledText className='mb-2' fontWeight={600} fontSize={isResponsive ? '12px' : '14px'} textColor={theme.CorporateTheme.colors.headerColor}>Choose Color Palette</StyledText>
                                <div className='d-flex gap-3'>
                                    <StyledColorPicker
                                        title='Primary Color'
                                        isResponsive
                                        subTitle={<>Used for <strong>Main buttons</strong>, <strong>Headers</strong> and <strong>links</strong>.</>}
                                        color={values.primaryColor}
                                        setColor={(color) => { setFieldValue('primaryColor', color); setWebsiteDetails(prev => ({ ...prev, primaryColor: color })) }}
                                        className='col'
                                    />
                                    <StyledColorPicker
                                        title='Secondary Color'
                                        isResponsive
                                        subTitle={<>Used for <strong>Subheadings, borders,</strong> and <strong>icons</strong>.</>}
                                        color={values.secondaryColor}
                                        setColor={(color) => { setFieldValue('secondaryColor', color); setWebsiteDetails(prev => ({ ...prev, secondaryColor: color })) }}
                                        className='col'
                                    />
                                    <StyledColorPicker
                                        title='Text Color'
                                        isResponsive
                                        subTitle={<>Used for <strong>Body text, paragraphs,</strong> and <strong>descriptions</strong>.</>}
                                        color={values.textColor}
                                        setColor={(color) => { setFieldValue('textColor', color); setWebsiteDetails(prev => ({ ...prev, textColor: color })) }}
                                        className='col'
                                    />
                                </div>
                            </div>

                            {/* Font Family */}
                            <div className='py-1 mt-4'>
                                <StyledText className='mb-2' fontWeight={600} fontSize={isResponsive ? '12px' : '14px'} textColor={theme.CorporateTheme.colors.headerColor}>Choose Font Family</StyledText>
                                <div className='d-flex gap-3'>
                                    {['Times New Roman', 'Roboto', 'Montserrat', 'Poppins'].map((font) => (
                                        <FontPicker
                                            key={font}
                                            inputName='fontFamily'
                                            fontName={font}
                                            selected={values.fontFamily === font}
                                            onClick={() => { setFieldValue('fontFamily', font); setWebsiteDetails(prev => ({ ...prev, fontFamily: font })) }}
                                            isResponsive
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </SecCard>
                );
            }}
        </Formik>
    );
};

export default SecSettings;